import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { HandHeart, Heart, Users, Gift, Calendar, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../context/LanguageContext";

const COPY = {
  en: {
    overline: "Get Involved",
    title: "Every Role Moves a Survivor Forward.",
    subtitle: "Volunteer, sponsor, donate, or partner — there is meaningful work for everyone in the community. Pick a path below and we'll take it from there.",
    paths: [
      { title: "Volunteer", desc: "Give your time to community outreach, workforce coaching, events, or administrative support.", cta: "Volunteer Interest" },
      { title: "Donate", desc: "Support emergency assistance, transportation, workforce training, and program capacity.", cta: "Donate" },
      { title: "Partner With Us", desc: "Referral partnerships, co-hosted trainings, program sponsorships, and grant partnerships.", cta: "Funders & Partners" },
      { title: "In-Kind Donations", desc: "Professional and interview clothing, hygiene items, essential supplies, and training materials.", cta: "Contact Us" },
      { title: "Community Resource Fair", desc: "Join as a resource partner, sponsor, volunteer, or attendee for our planned resource fair.", cta: "Express Interest" },
    ],
  },
  es: {
    overline: "Participa",
    title: "Cada Rol Impulsa a un Sobreviviente.",
    subtitle: "Voluntariado, patrocinio, donaciones o colaboración — hay trabajo significativo para toda la comunidad. Elige una vía abajo y nos encargamos del resto.",
    paths: [
      { title: "Voluntariado", desc: "Dona tu tiempo a alcance comunitario, coaching laboral, eventos o apoyo administrativo.", cta: "Interés en Voluntariado" },
      { title: "Donar", desc: "Apoya la asistencia de emergencia, transporte, capacitación laboral y capacidad del programa.", cta: "Donar" },
      { title: "Colabora con Nosotros", desc: "Colaboraciones de referencia, capacitaciones conjuntas, patrocinios de programas y colaboraciones de subvenciones.", cta: "Financiadores y Socios" },
      { title: "Donaciones en Especie", desc: "Ropa profesional y para entrevistas, artículos de higiene, suministros esenciales y materiales de capacitación.", cta: "Contáctanos" },
      { title: "Feria de Recursos Comunitarios", desc: "Únete como socio de recursos, patrocinador, voluntario o asistente para nuestra feria de recursos planificada.", cta: "Expresar Interés" },
    ],
  },
};

const ICONS = [HandHeart, Heart, Users, Gift, Calendar];
const HREFS = [
  "/get-involved/volunteer",
  "/donate",
  "/get-involved/funders",
  "mailto:director@thepillarprojectbaltimore.org?subject=In-Kind%20Donation",
  "/get-involved/community-fair",
];
const TESTIDS = ["gi-volunteer", "gi-donate", "gi-funders", "gi-inkind", "gi-fair"];

export default function GetInvolvedPage() {
  const { language } = useLanguage();
  const c = COPY[language] || COPY.en;

  return (
    <div>
      <section className="bg-warm-800 text-white py-16 md:py-24" data-testid="gi-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-warm-300 mb-4 block">{c.overline}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }} data-testid="gi-title">
              {c.title}
            </h1>
            <p className="text-lg text-warm-200 leading-relaxed max-w-2xl">{c.subtitle}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {c.paths.map((p, idx) => {
              const Icon = ICONS[idx];
              const href = HREFS[idx];
              const external = href.startsWith("mailto:");
              const Wrapper = ({ children }) => (external ? <a href={href}>{children}</a> : <Link to={href}>{children}</Link>);
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white border border-gray-100 rounded-xl p-6 flex flex-col"
                  data-testid={TESTIDS[idx]}
                >
                  <Icon className="w-9 h-9 text-warm-700 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "Cormorant Garamond, Georgia, serif" }}>{p.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed flex-grow mb-4">{p.desc}</p>
                  <Wrapper>
                    <Button variant="outline" className="w-full border-warm-700 text-warm-800 hover:bg-warm-50 rounded-full">
                      {p.cta} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Wrapper>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
